import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';
import { refreshTokens } from '@/lib/api/tokenRefresh';
import { resetZumoTokenState } from '@/lib/medis/zumoToken';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { AuthTokens } from '@/features/auth/types/auth.types';
import { patientsQueryKey } from '@/features/patients/hooks/usePatients';
import type { AddPatientInput } from '@/features/patients/types/patient.types';
import {
  REGISTER_SCENARIOS,
  SIMULATED_INPUT,
  setActiveScenario,
  type RegisterScenario,
} from './registerScenarios';

interface DevScenarioMenuProps {
  /** Fires the real add-patient mutation (the form's instance) with the given input. */
  run: (input: AddPatientInput) => void;
}

/**
 * Short identifiers of the EasyAuth session(s) a refresh issued: `host: …last8`
 * per MEDIS host (the grant covers the production host; the other
 * hosts are issued on demand at their next call), or just `…last8` for a
 * legacy session that carries the single token only. Empty when the refresh
 * failed or issued no EasyAuth token at all.
 */
function zumoSessionLabels(tokens: AuthTokens | null): string[] {
  if (tokens?.xZumoAuthHosts) {
    return Object.entries(tokens.xZumoAuthHosts).map(
      ([host, token]) => `${host}: …${token.slice(-8)}`,
    );
  }
  return tokens?.xZumoAuth ? [`…${tokens.xZumoAuth.slice(-8)}`] : [];
}

/**
 * Dev-only test panel for the RegisterPatient flow. Picking a scenario arms the
 * HTTP-level mock (see `registerScenarios.ts`) and immediately fires the real
 * mutation with a dummy payload — everything from hashing to the result banner
 * runs the production code path, only the wire response is canned. While a
 * scenario is active, manual form submits are intercepted too, until
 * "Oprește simularea". The ZUMO session reset lives here as well: a token
 * refresh co-issues a fresh X-ZUMO-AUTH token for the registry's default v2
 * host, and the hosts issued on demand are forgotten so their next call gets
 * a fresh one too ⇒ new EasyAuth sessions everywhere, which resets the stage
 * register sandbox (careful: the refresh token is single-use — on failure the
 * session clears and you must sign in again).
 */
export function DevScenarioMenu({ run }: DevScenarioMenuProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [scenario, setScenario] = useState<RegisterScenario | null>(null);
  const [zumoBusy, setZumoBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  if (!env.enableDevTools) return null;

  const activate = (s: RegisterScenario) => {
    setActiveScenario(s);
    setScenario(s);
    setOpen(false);
    setNote(null);
    // Register-mode scenarios need an empty patients cache so the hook
    // refetches GetPatient (mocked → 204) and computes attachPatient=false.
    if (s.noPatient) queryClient.removeQueries({ queryKey: patientsQueryKey });
    run(SIMULATED_INPUT);
  };

  const deactivate = () => {
    setActiveScenario(null);
    setScenario(null);
    setOpen(false);
    setNote(null);
    // Drop every mocked response and refetch the real data.
    void queryClient.invalidateQueries();
  };

  const resetZumo = async () => {
    setOpen(false);
    setZumoBusy(true);
    setNote(null);
    const grant = await refreshTokens();
    // The grant renews only the production (default) host and the store keeps the
    // hosts issued on demand. Forget those too (and the failure memos), so their
    // next call opens a NEW EasyAuth session as well — stage included.
    if (grant) useAuthStore.getState().setTokens(grant);
    resetZumoTokenState();
    const sessions = zumoSessionLabels(grant);
    if (sessions.length) {
      await queryClient.invalidateQueries();
      setNote(`Sesiune ZUMO nouă (${sessions.join(', ')})`);
    } else {
      setNote('Refresh eșuat — sesiunea a fost curățată, autentifică-te din nou.');
    }
    setZumoBusy(false);
  };

  return (
    <div className="rounded-card border border-dashed border-line bg-muted-100 px-4 py-3 text-xs text-ink-mute">
      <div className="relative flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={zumoBusy}
          className="shrink-0 rounded-input bg-ink px-3 py-1.5 font-semibold text-white disabled:opacity-50"
        >
          {zumoBusy ? 'Se resetează…' : 'Scenarii de test (dev) ▾'}
        </button>
        <span>
          {scenario
            ? `Simulare activă: ${scenario.label} — apelurile RegisterPatient${scenario.noPatient ? '/GetPatient' : ''} sunt interceptate.`
            : (note ?? 'Doar în dev: simulează apelul și răspunsul RegisterPatient v2, sau resetează sesiunea ZUMO.')}
        </span>

        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
            <div className="absolute left-0 top-full z-20 mt-2 w-80 overflow-hidden rounded-card bg-surface py-1 shadow-card">
              <p className="px-3 py-2 font-semibold uppercase tracking-wide text-ink-mute">
                Simulează un răspuns
              </p>
              {REGISTER_SCENARIOS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => activate(s)}
                  className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-muted-100"
                >
                  {s.label}
                </button>
              ))}
              <div className="my-1 border-t border-muted-200" />
              {scenario && (
                <button
                  type="button"
                  onClick={deactivate}
                  className="block w-full px-3 py-2 text-left text-sm font-semibold text-danger hover:bg-muted-100"
                >
                  Oprește simularea (înapoi la apeluri reale)
                </button>
              )}
              <button
                type="button"
                onClick={() => void resetZumo()}
                className="block w-full px-3 py-2 text-left text-sm font-semibold text-primary hover:bg-muted-100"
              >
                Reset sesiune ZUMO (sesiune EasyAuth nouă)
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
